import React, { Component } from "react";
import { Col, Row, Select, Button, Popconfirm, Input, Form, Tooltip, Modal, InputNumber } from "antd";
import { overtimeService } from './../../redux/services';
import { allowedTypesByColumn, filterSectionsByColumnType } from "../../utils/integrationUtils";

const ColumnEnums = {};
const TypeEnums = {};

const mkRow = (over = {}) => ({
  column: '',
  eventCode: '',
  type: '',
  limit: 1,
  separator: '',
  ...over,
});

// normaliza valores de uma linha recebida (evita undefined/null)
const normalizeRow = (row = {}) => mkRow({
  column: row.column ?? '',
  eventCode: row.eventCode ?? '',
  type: row.type ?? '',
  limit: (row.limit ?? 1),
  separator: row.separator ?? '',
});

const allowedValorTypes = new Set([
  "DATA_YYYY_MM_DD",
  "DATA_YYYYMMDD",
  "DATA_DD_MM_AAAA",
  "DATA_DDMMAAAA",
  "DATA_DD_MM_AA",
  "DATA_MM_DD_AA",
  "DATA_MMM_DD_YYYY",
  "DATA_DD_MMM_YYYY",
  "DATA_DD_MMMM_YYYY",
]);

class ColumnTable extends Component {
  formRef = React.createRef();

  constructor(props) {
    super(props);

    const initialRows = Array.isArray(props.eventData) && props.eventData.length
      ? props.eventData.map(normalizeRow)
      : [mkRow()];

    this.state = {
      rows: initialRows,
      columnOptions: [],
      typeSections: [],
    };
  }

  componentDidMount() {
    this.getFormData();
  };

  componentDidUpdate(prevProps) {
    // se o pai atualizar eventData (ex.: chegou do backend), sincroniza aqui
    if (prevProps.eventData !== this.props.eventData) {
      const incoming = Array.isArray(this.props.eventData)
        ? this.props.eventData.map(normalizeRow)
        : [];
      this.setState({
        rows: incoming.length ? incoming : [mkRow()],
      });
    }
  }

  handleAddRow = () => {
    this.setState((prevState) => ({
      rows: [...prevState.rows, mkRow()],
    }));
  };

  handleRemoveRow = (index) => {
    this.setState((prevState) => {
      const next = prevState.rows.filter((_, i) => i !== index);
      // garante pelo menos 1 linha
      return { rows: next.length ? next : [mkRow()] };
    });
  };

  handleColumnChange = (value, index) => {
    this.setState((prevState) => ({
      rows: prevState.rows.map((row, i) => {
          if (i === index) {
            const hasChangedColumn = row.column !== value;
            const defaultType = row.type && row.type !== '' && !hasChangedColumn ? row.type : allowedTypesByColumn[value]?.defaultValue || undefined;
            return { ...row, column: value, /* reset campo dependente se precisar: */ type: defaultType }
          } else {
            return row;
          }
        }
      ),
    }));
  };

  handleTypeChange = (value, index) => {
    this.setState((prevState) => ({
      rows: prevState.rows.map((row, i) =>
        i === index ? { ...row, type: value } : row
      ),
    }));
  };

  handleEventCodeChange = (value, index) => {
    this.setState((prevState) => ({
      rows: prevState.rows.map((row, i) =>
        i === index ? { ...row, eventCode: value } : row
      ),
    }));
  };

  handleLimitChange = (index, value) => {
    this.setState((prevState) => ({
      rows: prevState.rows.map((row, i) =>
        i === index ? { ...row, limit: value ?? 1 } : row
      ),
    }));
  }

  handleSeparatorChange = (index, event) => {
    const { value } = event.target;
    this.setState((prevState) => ({
      rows: prevState.rows.map((row, i) =>
        i === index ? { ...row, separator: value } : row
      ),
    }));
  }

  getFormData = async () => {
    const currentRows = Array.isArray(this.state?.rows) && this.state.rows.length
      ? this.state.rows
      : [mkRow()];

    try {
      const [colRes, typeRes] = await Promise.all([
        overtimeService.getColumnItems(),
        overtimeService.getTypesValue(),
      ]);

      const columnOptions = (colRes?.data ?? [])
        .filter(sec => sec?.title && Array.isArray(sec?.data))
        .map(sec => ({
          title: sec.title,
          options: (sec.data ?? [])
            .filter(i => (i?.label || i?.name) && i?.value != null)
            .map(i => ({
              value: i.value,
              label: i.label ?? i.name
            })),
        }));

      const typeSections = (typeRes?.data ?? [])
        .filter(sec => sec?.title && Array.isArray(sec?.data))
        .map(sec => ({
          title: sec.title,
          options: (sec.data ?? [])
            .filter(i => (i?.label || i?.name) && i?.value != null)
            .map(i => ({
              value: i.value,
              label: i.label ?? i.name,
              example: i.example ?? i.exemple,
            })),
        }));

      const normalizedRows = currentRows.map(normalizeRow);

      this.setState({
        columnOptions,
        typeSections,
        rows: normalizedRows.length ? normalizedRows : [mkRow()],
      });

      return normalizedRows.map(row => ({
        column: row.column,
        eventCode: row.eventCode,
        type: row.type,
        limit: row.limit,
        separator: row.separator,
      }));
    } catch (err) {
      Modal.error({
        title: 'Erro ao carregar dados',
        content: String(err),
      });

      const safeRows = currentRows.map(normalizeRow);

      return safeRows.map(row => ({
        column: row.column,
        eventCode: row.eventCode,
        type: row.type,
        limit: row.limit,
        separator: row.separator,
      }));
    }
  };

  render() {
    const { rows, columnOptions, typeSections } = this.state;
    const { readOnly } = this.props;

    return (
      <>
        <Form ref={this.formRef}>
          <>
            <Row gutter={[16, 16]} style={{ marginBottom: "5px" }}>
              <Col span={7}>
                <strong style={{ color: "#4C4C4C", fontSize: "14px" }}>
                  <span style={{ color: "#ff4d4f", fontSize: "14px" }}>*</span>{" "}
                  Coluna
                </strong>
              </Col>
              <Col span={7}>
                <strong style={{ color: "#4C4C4C", fontSize: "14px" }}>
                  <span style={{ color: "#ff4d4f", fontSize: "14px" }}>*</span>{" "}
                  Formato do Valor
                </strong>
              </Col>
              <Col span={4}>
                <strong style={{ color: "#4C4C4C", fontSize: "14px" }}>
                  <span style={{ color: "#ff4d4f", fontSize: "14px" }}>*</span>{" "}
                  Tamanho Campo
                </strong>
              </Col>
              <Col span={4}>
                <strong style={{ color: "#4C4C4C", fontSize: "14px" }}>
                  <span style={{ color: "#ff4d4f", fontSize: "14px" }}></span>{" "}
                  Separador de Coluna
                </strong>
              </Col>
              {!readOnly && (
                <Col span={1}>
                  <strong style={{ color: "#4C4C4C", fontSize: "14px" }}>
                    <span style={{ color: "#ff4d4f", fontSize: "14px" }}></span>{" "}
                  </strong>
                </Col>
              )}
            </Row>
            {rows.map((row, index) => (
              <Row
                gutter={[16, 16]}
                key={index}
                style={{
                  marginBottom: index === rows.length - 1 ? "10px" : "5px",
                }}
              >
                <Col span={7}>
                  <Select
                    showSearch
                    style={{
                      width: "100%",
                      marginBottom: index === rows.length - 1 ? "5px" : "10px",
                    }}
                    placeholder="Selecione uma coluna"
                    value={row.column}
                    onChange={(value) => this.handleColumnChange(value, index)}
                    disabled={readOnly}
                    optionFilterProp="label"
                    filterOption={(input, option) =>
                      (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                  >
                    {(columnOptions ?? []).map((sec) => (
                      <Select.OptGroup key={sec.title} label={sec.title}>
                        {(sec.options ?? []).map((t) => (
                          <Select.Option key={t.value} value={t.value}>
                            <Tooltip title={t.example}>
                              <div style={{ width: "100%" }}>{t.label}</div>
                            </Tooltip>
                          </Select.Option>
                        ))}
                      </Select.OptGroup>
                    ))}
                  </Select>
                </Col>

                <Col span={7}>
                  {row.column === "TEXTO_FIXO" ? (
                    <Input
                      style={{
                        width: "100%",
                        marginBottom: index === rows.length - 1 ? "5px" : "10px",
                      }}
                      placeholder="Digite um texto"
                      value={row.eventCode}
                      onChange={(e) => this.handleEventCodeChange(e.target.value, index)}
                      disabled={readOnly}
                    />
                  ) : (
                    <Select
                      style={{
                        width: "100%",
                        marginBottom: index === rows.length - 1 ? "5px" : "10px",
                      }}
                      placeholder="Selecione o Formato da coluna"
                      // se for VALOR e o tipo atual não é permitido, zera o value pra evitar opção inválida
                      value={
                        // row.column === "VALOR" && row.type && !allowedValorTypes.has(row.type)
                        //   ? undefined
                        //   : row.type
                        row.type && row.type !== '' ? row.type : allowedTypesByColumn[row.column]?.defaultValue || undefined
                      }
                      onChange={(value) => this.handleTypeChange(value, index)}
                      disabled={readOnly}
                      optionFilterProp="label"
                      filterOption={(input, option) =>
                        (option?.children ?? "")
                          .toString()
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                    >
                      {(
                        (typeSections ?? [])
                          .map((sec) => {
                            const filteredOptions = filterSectionsByColumnType(row.column, sec.options, 'options');
                            return { ...sec, options: filteredOptions };
                          })
                          // remove grupos vazios
                          .filter((sec) => (sec.options ?? []).length > 0)
                      ).map((sec) => (
                        <Select.OptGroup key={sec.title} label={sec.title}>
                          {(sec.options ?? []).map((t) => (
                            <Select.Option key={t.value} value={t.value}>
                              <Tooltip title={t.example}>
                                <div style={{ width: "100%" }}>{t.label}</div>
                              </Tooltip>
                            </Select.Option>
                          ))}
                        </Select.OptGroup>
                      ))}
                    </Select>
                  )}
                </Col>

                <Col span={4}>
                  <InputNumber
                    style={{ width: "100%" }}
                    min={1}
                    value={row.limit}
                    onChange={(val) => this.handleLimitChange(index, val)}
                    disabled={readOnly}
                  />
                </Col>

                <Col span={4}>
                  <Input
                    value={row.separator}
                    maxLength={1}
                    onChange={(event) => this.handleSeparatorChange(index, event)}
                    readOnly={readOnly}
                  />
                </Col>

                {!readOnly && (
                  <Col span={1}>
                    {index > 0 && (
                      <Popconfirm
                        title="Deseja excluir essa linha?"
                        onConfirm={() => this.handleRemoveRow(index)}
                        okText="Sim"
                        cancelText="Não"
                      >
                        <Button
                          danger
                          shape="circle"
                          icon={<i className="fal fa-trash"></i>}
                        />
                      </Popconfirm>
                    )}

                    {index === rows.length - 1 && (
                      <Tooltip placement="top" title="Incluir nova linha">
                        <Button
                          style={{ marginTop: "10px" }}
                          onClick={this.handleAddRow}
                          type="default"
                          shape="circle"
                          icon={<i className="fas fa-plus"></i>}
                        />
                      </Tooltip>
                    )}
                  </Col>
                )}
              </Row>
            ))}
          </>
        </Form>
      </>
    );
  }
}

export default ColumnTable;
